import { WebPubSubClient } from "@azure/web-pubsub-client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCreateAssistantThreadMutation } from "../../graphql/generated/graphql";

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
};

type UseAssistantRealtimeOptions = {
  open: boolean;
  errorFallback: string;
};

export default function useAssistantRealtime({ open, errorFallback }: UseAssistantRealtimeOptions)
{
  const [threadId, setThreadId] = useState<string | null>(null);
  const [webPubSubUrl, setWebPubSubUrl] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingMessage, setStreamingMessage] = useState<string | null>(null);
  const webPubSubClientRef = useRef<WebPubSubClient | null>(null);
  const creatingThreadRef = useRef(false);
  const isThreadReady = Boolean(threadId && webPubSubUrl);

  const [createAssistantThread] = useCreateAssistantThreadMutation();

  const handleWebPubSubMessage = useCallback((payload: any) =>
  {
    const rawMessage = payload?.message?.data ?? payload?.message ?? payload?.data ?? payload;
    if(!rawMessage || typeof rawMessage !== 'object') return;

    const messageType = rawMessage.type as string | undefined;
    const messageData = rawMessage.data ?? {};

    if(messageType === 'assistant-thinking')
    {
      const partial = typeof messageData.partial === 'string' ? messageData.partial : '';
      setStreamingMessage(partial);
      return;
    }

    if(messageType === 'assistant-response')
    {
      const content = typeof messageData.message === 'string' ? messageData.message : '';
      if(content)
      {
        setMessages(prev => ([
          ...prev,
          { id: `assistant-${Date.now()}`, role: 'assistant', content, timestamp: new Date().toISOString() }
        ]));
      }
      setStreamingMessage(null);
      return;
    }

    if(messageType === 'error')
    {
      const content = typeof messageData.message === 'string' ? messageData.message : errorFallback;
      setMessages(prev => ([
        ...prev,
        { id: `error-${Date.now()}`, role: 'system', content, timestamp: new Date().toISOString() }
      ]));
      setStreamingMessage(null);
    }
  }, [errorFallback]);

  const connectWebPubSub = useCallback(async (url: string, thread: string) =>
  {
    const client = new WebPubSubClient(url, { autoReconnect: true });
    webPubSubClientRef.current = client;

    client.on('group-message', handleWebPubSubMessage);

    await client.start();
    await client.joinGroup(`assistant_thread_${thread}`);
  }, [handleWebPubSubMessage]);

  const disconnectWebPubSub = useCallback(async () =>
  {
    const client = webPubSubClientRef.current;
    if(!client) return;

    try
    {
      client.off('group-message', handleWebPubSubMessage);
      await client.stop();
    }
    finally
    {
      webPubSubClientRef.current = null;
    }
  }, [handleWebPubSubMessage]);

  useEffect(() =>
  {
    if(!open)
    {
      setThreadId(null);
      setWebPubSubUrl(null);
      setMessages([]);
      setStreamingMessage(null);
      disconnectWebPubSub();
      return;
    }

    if(creatingThreadRef.current) return;
    creatingThreadRef.current = true;

    (async () =>
    {
      try
      {
        const { data } = await createAssistantThread();
        const thread = data?.CreateAssistantThread?.ThreadId as string | undefined;
        const url = data?.CreateAssistantThread?.WebPubSubUrl as string | undefined;

        if(thread && url)
        {
          setThreadId(thread);
          setWebPubSubUrl(url);
          setMessages([]);
          setStreamingMessage(null);
          await connectWebPubSub(url, thread);
        }
      }
      finally
      {
        creatingThreadRef.current = false;
      }
    })();
  }, [open, createAssistantThread, connectWebPubSub, disconnectWebPubSub]);

  const addMessage = useCallback((message: ChatMessage) =>
  {
    setMessages(prev => ([...prev, message]));
  }, []);

  return {
    threadId,
    isThreadReady,
    messages,
    streamingMessage,
    addMessage,
  };
}
